// Transparent header: Logo + optional LiveNow (left cluster),
// HeaderNav + HamburgerMenu (right cluster).
// Mount once at the app shell via HeaderLayout — keeps the logo/nav stable across route changes.

import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import Logo from './Logo';
import LiveNow from './LiveNow';
import HamburgerMenu from './HamburgerMenu';
import HeaderNav from './HeaderNav';
import { useHomeNavAccent } from '../../hooks/useHomeNavAccent';
import { getHomeBeatHeaderScopeStyle } from './headerAccentMap';
import { getRouteHeaderTraits } from '@/app/routing/getFrontendRoutes';

const Header: React.FC = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const homeNavAccent = useHomeNavAccent();

  const traits = useMemo(
    () => getRouteHeaderTraits(location.pathname),
    [location.pathname]
  );
  const showLiveNow = Boolean(traits.liveNow);
  const showBgSolid = !traits.transparent;

  return (
    <>
      {showBgSolid && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-x-0 top-0 z-[200] h-20 bg-background sm:h-24 lg:h-28"
        />
      )}

      <header className="pointer-events-none fixed inset-x-0 top-0 z-[210]">
        <div
          className="flex items-center justify-between gap-4 px-4 pt-4 sm:px-6 sm:pt-5 lg:px-6 lg:pt-6 xl:px-8"
          style={
            isHomePage && homeNavAccent != null
              ? getHomeBeatHeaderScopeStyle(homeNavAccent)
              : undefined
          }
        >
          {/* Left cluster: Logo + LiveNow. Tight gap at base so the compact
              pill doesn't crowd the hamburger; gap ≥ Logo's internal icon↔wordmark
              gap (gap-2 sm:gap-3 lg:gap-4) from sm+. */}
          <div className="pointer-events-auto flex min-w-0 shrink items-center gap-2 sm:gap-5 lg:gap-6">
            <Logo
              className="shrink-0"
              beatTint={isHomePage && homeNavAccent != null}
            />
            {showLiveNow && <LiveNow className="translate-y-[1px]" />}
          </div>

          <div className="pointer-events-auto flex shrink-0 items-center gap-2 sm:gap-3 lg:gap-4">
            <HeaderNav tone={isHomePage ? 'home' : 'solid'} />
            <HamburgerMenu tone={isHomePage ? 'home' : 'solid'} />
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
